/**
 * WorkLogix Phase 2 — Photo-on-Punch: the SHARED contract for the verification
 * photo that attaches to an immutable kiosk punch (plan-v7). Pure and
 * dependency-free (no Nest, no pg, no React and — deliberately — NO browser/DOM
 * APIs) so the kiosk PWA (capture + downscale), the API (upload / read / proxy)
 * and the supervisor SPA (review) all import the SAME types and the SAME
 * downscale math.
 *
 * The photo attaches to the PUNCH (`app.kiosk_punch.id`), never to the derived
 * `time_session`. A `missing` record is a first-class outcome (camera
 * unavailable / denied / capture failed): the punch is NEVER lost, delayed or
 * blocked by anything on the photo path.
 */
// ── Pure downscale planning (browser-free; consumed by the kiosk + its tests) ──
/** Default longest-edge cap (px) for the on-device downscale (plan-v7). */
export const DEFAULT_MAX_EDGE = 1280;
/** Default JPEG quality for the on-device encode (plan-v7, ~200 KB target). */
export const DEFAULT_QUALITY = 0.7;
/** Reject a non-positive / non-finite pixel dimension with a clear, actionable error. */
function assertPositiveDimension(value, label) {
    if (!Number.isFinite(value) || value <= 0) {
        throw new Error(`computeDownscalePlan: ${label} must be a positive, finite number (got ${String(value)})`);
    }
}
/**
 * Clamp a requested JPEG quality into the half-open interval (0, 1]:
 *   - values above 1 clamp DOWN to 1;
 *   - values at / below 0 clamp UP to the smallest positive double, so the result
 *     never leaves the open lower bound (there is no representable 0-exclusive
 *     floor to snap to);
 *   - `NaN` is rejected (it cannot be clamped meaningfully).
 * Never throws for a finite quality — only dimensions are degenerate-throwing.
 */
function clampQuality(quality) {
    if (Number.isNaN(quality)) {
        throw new Error(`computeDownscalePlan: quality must be a number (got ${String(quality)})`);
    }
    if (quality > 1) {
        return 1;
    }
    if (quality <= 0) {
        return Number.MIN_VALUE;
    }
    return quality;
}
/**
 * Compute a deterministic, browser-free downscale plan for an on-device JPEG
 * re-encode:
 *   - preserves aspect ratio;
 *   - caps the LONGEST edge at `maxEdge` (default {@link DEFAULT_MAX_EDGE});
 *   - NEVER upscales — a source already within `maxEdge` is returned unchanged;
 *   - rounds target pixels to whole integers (floored at 1 so a degenerate
 *     aspect never yields a 0-pixel edge);
 *   - clamps `quality` into (0, 1] (default {@link DEFAULT_QUALITY}).
 * Throws on a 0 / negative / non-finite source dimension or `maxEdge`.
 */
export function computeDownscalePlan(srcW, srcH, opts = {}) {
    assertPositiveDimension(srcW, 'srcW');
    assertPositiveDimension(srcH, 'srcH');
    const maxEdge = opts.maxEdge ?? DEFAULT_MAX_EDGE;
    assertPositiveDimension(maxEdge, 'maxEdge');
    const quality = clampQuality(opts.quality ?? DEFAULT_QUALITY);
    const longestEdge = Math.max(srcW, srcH);
    // Never upscale: only shrink when the source actually exceeds the cap.
    const scale = longestEdge > maxEdge ? maxEdge / longestEdge : 1;
    const targetW = Math.max(1, Math.round(srcW * scale));
    const targetH = Math.max(1, Math.round(srcH * scale));
    return { targetW, targetH, quality };
}
//# sourceMappingURL=photo.js.map